// Regroup panel — M5 prototype answer to "flat shape soup" CAD.
//
// When the source CAD has no useful assembly hierarchy (single part with N
// mesh chunks, like the GIS GP-series files), a Pogodan engineer uses this
// UI to manually assign each chunk to a named group. The output is a
// regroup.json sidecar that the apply-regroup CLI consumes to emit a new
// glTF with proper hierarchy.

import type { LoadedNode, ViewerController } from './viewer-controller.js';

export interface Regrouping {
  /** Map of group name → list of leaf node ids assigned to it. */
  groups: Map<string, Set<string>>;
}

export interface RegroupPanelOptions {
  container: HTMLElement;
  viewer: ViewerController;
  /** All leaf (synthetic mesh-wrapper) nodes available for assignment. */
  leafNodes: LoadedNode[];
  /** Assembly slug, written into the saved JSON. */
  assemblyName: string;
}

const GROUP_PALETTE = [
  '#e07b3c',
  '#5d9c6b',
  '#5a7bb1',
  '#a64b8e',
  '#d2b13a',
  '#6f4e7c',
  '#3f7d8e',
  '#c45757',
];

export function mountRegroupPanel(opts: RegroupPanelOptions): {
  dispose(): void;
  applyPickedLeaf(leafId: string | null): void;
} {
  const { container, viewer, leafNodes, assemblyName } = opts;

  // State.
  const assignments = new Map<string, string>(); // leafId → groupName
  const selectedLeafIds = new Set<string>();

  container.innerHTML = '';
  const panel = document.createElement('div');
  panel.className = 'regroup-inner';
  container.appendChild(panel);

  // Header: input + assign + save.
  const header = document.createElement('div');
  header.className = 'regroup-header';
  panel.appendChild(header);

  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.placeholder = 'Group name…';
  nameInput.className = 'regroup-name-input';
  header.appendChild(nameInput);

  const assignBtn = document.createElement('button');
  assignBtn.textContent = 'Assign';
  assignBtn.className = 'regroup-assign-btn';
  header.appendChild(assignBtn);

  const saveBtn = document.createElement('button');
  saveBtn.textContent = 'Save regrouping';
  saveBtn.className = 'regroup-save-btn';
  panel.appendChild(saveBtn);

  // List of leaf nodes.
  const list = document.createElement('ul');
  list.className = 'regroup-list';
  panel.appendChild(list);

  const rowsByLeafId = new Map<string, HTMLLIElement>();
  for (const leaf of leafNodes) {
    const li = document.createElement('li');
    li.dataset.leafId = leaf.id;
    li.className = 'regroup-row';

    const labelWrap = document.createElement('span');
    labelWrap.className = 'regroup-label';
    labelWrap.textContent = leaf.id;
    li.appendChild(labelWrap);

    const groupTag = document.createElement('span');
    groupTag.className = 'regroup-group-tag';
    groupTag.textContent = '—';
    li.appendChild(groupTag);

    li.addEventListener('click', () => {
      toggleSelect(leaf.id);
    });
    list.appendChild(li);
    rowsByLeafId.set(leaf.id, li);
  }

  function toggleSelect(leafId: string): void {
    if (selectedLeafIds.has(leafId)) selectedLeafIds.delete(leafId);
    else selectedLeafIds.add(leafId);
    redraw();
    viewer.selectNodes(Array.from(selectedLeafIds));
  }

  function applyPickedLeaf(leafId: string | null): void {
    if (!leafId) {
      selectedLeafIds.clear();
    } else {
      // Append to selection (additive picking — matches multi-select UX).
      if (selectedLeafIds.has(leafId)) selectedLeafIds.delete(leafId);
      else selectedLeafIds.add(leafId);
    }
    redraw();
    viewer.selectNodes(Array.from(selectedLeafIds));
  }

  function colourForGroup(name: string): string {
    if (!name) return '#999';
    const groupNames = Array.from(new Set(assignments.values())).sort();
    const idx = groupNames.indexOf(name);
    return GROUP_PALETTE[idx % GROUP_PALETTE.length] ?? '#999';
  }

  function redraw(): void {
    for (const [leafId, li] of rowsByLeafId) {
      const isSelected = selectedLeafIds.has(leafId);
      const group = assignments.get(leafId);
      li.classList.toggle('selected', isSelected);
      const tag = li.querySelector<HTMLElement>('.regroup-group-tag');
      if (tag) {
        if (group) {
          tag.textContent = group;
          tag.style.background = colourForGroup(group);
          tag.style.color = '#fff';
        } else {
          tag.textContent = '—';
          tag.style.background = 'transparent';
          tag.style.color = '#888';
        }
      }
    }
  }

  assignBtn.addEventListener('click', () => {
    const name = nameInput.value.trim();
    if (!name) return;
    if (selectedLeafIds.size === 0) return;
    for (const id of selectedLeafIds) assignments.set(id, name);
    selectedLeafIds.clear();
    nameInput.value = '';
    redraw();
    viewer.clearSelection();
  });

  nameInput.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') assignBtn.click();
  });

  saveBtn.addEventListener('click', () => {
    const groups = new Map<string, string[]>();
    for (const [leafId, groupName] of assignments) {
      const arr = groups.get(groupName) ?? [];
      arr.push(leafId);
      groups.set(groupName, arr);
    }
    const payload = {
      schemaVersion: '1' as const,
      sourceAssembly: assemblyName,
      groups: Array.from(groups, ([name, meshNodeIds]) => ({ name, meshNodeIds })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `regroup-${assemblyName}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  });

  redraw();

  return {
    dispose(): void {
      container.innerHTML = '';
    },
    applyPickedLeaf,
  };
}
